import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {NgForOf, NgIf} from "@angular/common";
import {LayoutService} from "../../service/layout.service";
import {SidenavService} from "../../service/sidenav.service";
import {ThemeService} from "../../service/theme.service";
import {MidiPlayerService} from "../../service/midi-player.service";
import {HymnSelectionService} from "../../service/hymn-selection.service";
import {SettingsService} from "../../service/settings.service";
import {Hymn} from "../../hymn/hymn";
import {GameComponent} from "../game/game.component";
import {BackButtonService} from "../../service/back-button.service";
import {ConfettiService} from "../../service/confetti.service";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {DialpadComponent} from "../../controls/dialpad/dialpad.component";
import {HymnListComponent} from "../modal/hymn-list/hymn-list.component";
import {translation} from "../../translation";

//define extension of Hymn with status field
export interface HymnWithStatus extends Hymn {
  status: string;
}

@Component({
  selector: 'app-single-player',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, GameComponent, NgForOf],
  templateUrl: './single-player.component.html',
  styleUrl: './single-player.component.scss'
})
export class SinglePlayerComponent implements OnInit, OnDestroy {

  @Output() exit = new EventEmitter<void>();
  @Output() score = new EventEmitter<number>();

  @Input() topScore: number = 0;
  @Input() favorites: boolean = false;

  @ViewChild('player1') player1!: GameComponent;

  round = -1;
  hymns: Hymn[] = [];
  playButtonActive = false;

  player1Selected = false;
  player1Answer?: Hymn;

  player1Correct = false;

  player1score = 0;

  options: HymnWithStatus[] = [];
  winningOption?: Hymn;
  startTime = 0;
  countdown = 3;

  loading = true;

  timeouts: any = [];

  heartTotal = 3;
  heartCount = this.heartTotal;
  hearts = this.calculateHearts();
  selectionMade = false;

  player1Message = translation.guess_the_hymn.single_player_instruction;

  active = true;

  private calculateHearts(): boolean[] {
    // create an array of booleans to represent the hearts
    const hearts = [];
    for (let i = this.heartTotal-1; i >= 0; i--) {
      hearts.push(i < this.heartCount);
    }
    return hearts;
  }

  private deductHeart() {
    this.heartCount--;
    this.hearts = this.calculateHearts();
  }

  constructor(private layoutService: LayoutService, private sidenavService: SidenavService,
              private themeService: ThemeService, protected midiPlayerService: MidiPlayerService,
              private hymnSelectionService: HymnSelectionService, private settingsService: SettingsService,
              private backButtonService: BackButtonService, private confettiService: ConfettiService,
              private dialog: MatDialog) {
    // --- NEW UNIFIED EVENT STREAM SUBSCRIPTION ---
    this.midiPlayerService.event$.subscribe(ev => {
      if (ev?.type === 'status') {
        if (ev.status === 'playing') {
          this.playButtonActive = false;
        } else if (!this.player1Selected) {
          this.playButtonActive = true;
        }
      }
    });
  }

  ngOnInit(): void {
    this.active = true;
    this.initialize()
  }

  ngOnDestroy() {
    this.active = false;
    this.midiPlayerService.stop();
  }

  async resetState() {
    this.hymns = this.hymnSelectionService.getRandomList(this.favorites);
    this.player1score = 0;
    this.round = -1;
    this.countdown = 3;
    this.player1Answer = undefined;
    this.player1Correct = false;
    this.heartCount = this.heartTotal;
    this.hearts = this.calculateHearts();
  }

  async initialize() {
    this.resetState();
    this.backButtonService.registerBackButtonAction(() => {
      this.onExitDecideOutcome();
    });
  }

  async begin() {
    this.resetState();
    this.midiPlayerService.initAudioContext();
    this.nextRound(false);
  }

  async nextRound(wait: boolean = true) {
    this.round++;
    this.countdown = 3;
    this.player1Selected = false;
    this.player1Answer = undefined;
    this.player1Correct = false;

    this.loading = true;
    if(wait) {
      await this.performCountdown();
    }
    // Remove the next four hymns from the list

    if(this.round >= this.hymns.length) {
      this.hymns = this.hymnSelectionService.getRandomList(false);
    }

    this.winningOption = this.hymns[this.round];
    this.options = this.generateOptions(this.winningOption).map(h => ({...h, status: 'none'}));

    if(this.winningOption?.hasChorus && this.winningOption.timestamps?.chorus
      && Math.floor(Math.random() * 2) == 0) { // 50% chance to play chorus
      this.startTime = this.winningOption.timestamps.chorus;
    } else {
      this.startTime = 0;
    }

    const player1Options = this.options.map(o => ({...o, status: 'none'}));

    // Ensure a different order for player 2 to avoid players seeing each other's choices
    const player2Options = this.hymnSelectionService.shuffleArray(this.options.map(o => ({...o, status: 'none'})));

    this.player1Message = translation.guess_the_hymn.which_hymn;
    await this.player1.nextRound(player1Options, this.winningOption, this.startTime);

    this.loading = false;

    if(this.active) {
      if (!this.midiPlayerService.hasAudioContext()) {
        this.playButtonActive = true;
      } else {
        this.playButtonActive = false;
        this.playMidi();
      }
    }
  }

  generateOptions(winningOption: Hymn, extraOptions = 3): Hymn[] {
    const options = [winningOption];
    while (options.length <= extraOptions) {
      const randomIndex = Math.floor(Math.random() * this.hymns.length);
      const randomHymn = this.hymns[randomIndex];
      // check that the current options don't contain this option
      if (!options.some(o => o.hymnNumber === randomHymn.hymnNumber)) {
        options.push(randomHymn);
      }
    }
    return this.hymnSelectionService.shuffleArray(options);
  }

  async performCountdown() {
    for (let i = this.countdown; i > 0; i--) {
      await new Promise(resolve => {
        this.timeouts.push(setTimeout(resolve, 1000));
      });
      this.countdown--;
    }
  }

  playMidi(startTime: number = this.startTime) {
    this.midiPlayerService.playHymnPieceForGame(this.winningOption!, 100, false, this.startTime);
  }

  onPlayerOptionSelected(chosenOption: Hymn) {

    this.midiPlayerService.initAudioContext();

    this.player1Selected = true;
    this.player1Answer = chosenOption;
    this.player1Correct = chosenOption.hymnNumber === this.winningOption?.hymnNumber;

    if(this.player1Selected) {
      this.decideRoundOutcome();

      if(this.heartCount <= 0) {
        this.player1.setDisableOptions(true);
        this.player1.showPlayer();

        setTimeout(() => {
          this.decideFinalOutcome();
        }, 2000);
      } else {
        // Only stop the midi at the end of the round, not when a player has won
        this.midiPlayerService.stop();

        this.player1.setDisableOptions(true);
        this.player1.showPlayer();

        this.nextRound();
      }
    }
  }

  private decideFinalOutcome() {
    this.score.emit(this.player1score);
    if (this.player1score <= this.topScore) {
      if (this.player1score > this.topScore*0.6) {
        this.player1.win(translation.guess_the_hymn.well_done, false, true, 1);
      } else {
        this.player1.lose(translation.guess_the_hymn.game_over);
      }
    } else {
      this.player1.win(translation.guess_the_hymn.new_high_score, false, true, 10);
      this.player1.showPointsAward("🏆");
    }
  }

  private decideRoundOutcome() {
    const roundDuration = this.player1.getRoundDuration();

    let hintDetuctions = 0;
    if(this.player1.hintUsed5050) {
      hintDetuctions++;
    }
    if(this.player1.hintUsedLyrics) {
      hintDetuctions++;
    }


    let resultMessage = [];
    if(this.player1Correct) {
      if(roundDuration < 5000) {
        if (hintDetuctions === 0) {
          resultMessage.push(translation.guess_the_hymn.fast);
          this.player1score += 5;
          this.player1.showPointsAward("+5");
        } else if (hintDetuctions === 1) {
          this.player1score += 3;
          this.player1.showPointsAward("+3");
        } else {
          this.player1.showPointsAward("+1");
        }
      } else {
        resultMessage.push(translation.guess_the_hymn.correct_single);

        if (hintDetuctions === 0) {
          this.player1score += 3;
          this.player1.showPointsAward("+3");
        } else if (hintDetuctions === 1) {
          this.player1score += 1;
          this.player1.showPointsAward("+1");
        } else {
          this.player1.showPointsAward("+0");
        }
      }
    } else {
      resultMessage.push(translation.guess_the_hymn.incorrect_single);
      this.player1.showPointsAward("💔");
      this.deductHeart();
    }

    this.player1Message = resultMessage[0];
    this.player1.setDarkBackground(!this.player1Correct);
  }

  protected readonly Math = Math;

  stopMidi() {
    this.midiPlayerService.stop();
  }

  onExitDecideOutcome() {
    this.decideFinalOutcome();
    this.timeouts.forEach((timeout: any) => {
      clearTimeout(timeout);
    });
    setTimeout(() => {
      this.onExit();
    }, 3000);
  }

  onExit() {
    this.timeouts.forEach((timeout: any) => {
      clearTimeout(timeout);
    });
    this.stopMidi();
    this.confettiService.stop();
    this.exit.emit();
  }

  onRestart() {
    this.stopMidi();
    this.begin();
  }

  openInfoDialog() {
    this.dialog.closeAll();
    const dialogRef = this.dialog.open(
      HymnListComponent,
      {data: this.hymns.slice(0, this.round+1)}
    );
    this.active = true;
    dialogRef.afterClosed().subscribe(() => {
      this.active = true;
    })
  }

  protected readonly translation = translation;
}
